import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Analytics from "./components/Analytics";

// Lazy-loaded pages for code splitting
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Freight = lazy(() => import("./pages/Freight"));
const Sourcing = lazy(() => import("./pages/Sourcing"));
const Catalog = lazy(() => import("./pages/Catalog"));
const CatalogAdmin = lazy(() => import("./pages/CatalogAdmin"));
const Contact = lazy(() => import("./pages/Contact"));
const Success = lazy(() => import("./pages/Success"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AnimateOnScroll() {
  const { pathname } = useLocation();
  useEffect(() => {
    const timeout = setTimeout(() => {
      const elements = document.querySelectorAll(".animate-on-scroll:not(.is-visible)");
      if (elements.length === 0) return;
      const observer = new IntersectionObserver(
        (entries) => entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            observer.unobserve(e.target);
          }
        }),
        { threshold: 0.05, rootMargin: "0px 0px -20px 0px" }
      );
      elements.forEach((el) => {
        el.classList.add("will-animate");
        observer.observe(el);
      });
      (window as any).__scrollObserver = observer;
    }, 150);
    return () => {
      clearTimeout(timeout);
      (window as any).__scrollObserver?.disconnect();
    };
  }, [pathname]);
  return null;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="h-8 w-8 rounded-full border-2 border-teal border-t-transparent animate-spin" />
    </div>
  );
}

function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return <PageLoader />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AnimateOnScroll />
      <Analytics />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-navy focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg"
      >
        Skip to content
      </a>
      <Header />
      <main id="main" className="min-h-[60vh]">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/freight" element={<Freight />} />
            <Route path="/sourcing" element={<Sourcing />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/catalog/:id" element={<ProductDetail />} />
            <Route path="/admin/catalog" element={<CatalogAdmin />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/success" element={<Success />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/shiptogether" element={<ExternalRedirect to="https://ship-together.com" />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </BrowserRouter>
  );
}
